import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArtworkUpload } from "../components/ArtworkUpload";
import { SIGN_PRODUCTS, PULLUP_PRICE, PULLUP_QTYS, PULLUP_SIZE, formatAUDexact } from "../data/signsPricing";
import { Check } from "lucide-react";

export default function PullUpBuilder() {
  const info = SIGN_PRODUCTS.pullup;
  const [qty, setQty] = useState(1);
  const [customQty, setCustomQty] = useState("");
  const [file, setFile] = useState(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const isCustomQty = qty === "more";
  const effectiveQty = isCustomQty ? (parseInt(customQty, 10) || 0) : qty;
  const validQty = isCustomQty ? effectiveQty >= 5 : effectiveQty > 0;
  const subtotal = validQty ? effectiveQty * PULLUP_PRICE : null;
  const gst = subtotal != null ? subtotal * 0.1 : null;
  const total = subtotal != null ? subtotal + gst : null;

  const addToCart = () => {
    if (!file) { toast.error("Please upload your artwork first"); return; }
    if (isCustomQty && effectiveQty < 5) { toast.error("Please enter a quantity of 5 or more"); return; }
    addItem({
      product_type: "pullup",
      product_name: info.name,
      size: PULLUP_SIZE,
      quantity: effectiveQty,
      unit_price: PULLUP_PRICE,
      line_total: subtotal,
      print_sides: "single",
      artwork_file_id: file.id,
      artwork_filename: file.name,
    });
    toast.success(`${effectiveQty} × Pull-Up Banner added to cart`);
    navigate("/cart");
  };

  return (
    <div className="App">
      <Header />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-6">
        <div className="eyebrow" data-testid="sign-eyebrow">{info.shortName} · Custom print</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-2 tracking-tight">{info.headline}</h1>
        <p className="mt-4 text-lg text-slate-600 max-w-2xl">{info.tagline}</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-6 space-y-6">
          <div className="sf-card overflow-hidden">
            <div className="aspect-[5/4] relative bg-[#F6F8FD]">
              <img src={info.image} alt={info.imageAlt} className="w-full h-full object-cover" data-testid="sign-product-photo" />
              <div className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/80 backdrop-blur text-white text-[10px] font-bold uppercase tracking-widest">{info.imageTag}</div>
            </div>
          </div>
          <CheckList title="Ideal for" items={info.uses} grid />
          <CheckList title="About this product" items={info.features} />
        </div>

        <div className="lg:col-span-6 space-y-6">
          <div className="sf-card p-6 space-y-5">
            <div>
              <div className="eyebrow mb-3">Size</div>
              <div className="flex flex-wrap gap-2"><span className="chip chip-active" data-testid="pullup-size">{PULLUP_SIZE}</span></div>
              <p className="text-xs text-slate-500 mt-2">One standard size · full-colour, single-sided · stand & carry bag included.</p>
            </div>
            <div>
              <div className="eyebrow mb-3">Step 1 — Quantity</div>
              <div className="grid grid-cols-6 gap-2" data-testid="pullup-qty-options">
                {PULLUP_QTYS.map((q) => (
                  <button key={q} onClick={() => setQty(q)} className={`chip justify-center ${qty === q ? "chip-active" : ""}`} data-testid={`pullup-qty-${q}`}>{q}</button>
                ))}
                <button onClick={() => setQty("more")} className={`chip justify-center ${isCustomQty ? "chip-active" : ""}`} data-testid="pullup-qty-more">5+</button>
              </div>
              {isCustomQty && (
                <label className="block mt-3">
                  <span className="eyebrow block mb-1">Enter your quantity</span>
                  <input
                    type="number" inputMode="numeric" min="5" placeholder="e.g. 8"
                    value={customQty}
                    onChange={(e) => setCustomQty(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    className="w-full px-4 py-3 rounded-xl border border-[#E2DDD5] bg-white focus:outline-none"
                    data-testid="pullup-qty-more-input"
                  />
                </label>
              )}
              {isCustomQty && !validQty && <p className="text-xs text-slate-500 mt-2" data-testid="pullup-qty-more-hint">Enter 5 or more — still a flat $150 per banner.</p>}
            </div>
          </div>

          <div className="sf-card p-6">
            <div className="eyebrow mb-3">Step 2 — Upload artwork</div>
            <ArtworkUpload file={file} setFile={setFile} />
          </div>

          <div className="sf-card p-6">
            <div className="eyebrow">Live quote</div>
            <div className="mt-2 flex items-baseline gap-2">
              <div className="font-display text-5xl font-black price-tick" data-testid="sign-total-exgst">{formatAUDexact(subtotal)}</div>
              <div className="text-sm text-slate-500 font-semibold">ex GST</div>
            </div>
            <div className="text-slate-600 mt-1 text-sm" data-testid="sign-unit-price">{formatAUDexact(PULLUP_PRICE)} per banner × {effectiveQty || "—"}</div>
            <div className="mt-5 space-y-2 text-sm">
              <Row label="Product" value={info.name} testid="sign-summary-product"/>
              <Row label="Size" value={PULLUP_SIZE} testid="sign-summary-size"/>
              <Row label="Quantity" value={effectiveQty ? effectiveQty.toLocaleString() : "Enter quantity above"} testid="sign-summary-qty"/>
              <Row label="Print" value="Full-colour, single-sided" testid="sign-summary-sides"/>
              <Row label="Includes" value="Stand + carry bag" testid="sign-summary-includes"/>
              <Row label="Artwork" value={file ? file.name : "Not uploaded"} testid="sign-summary-artwork"/>
            </div>
            <div className="h-px bg-[#E2DDD5] my-4"/>
            <div className="space-y-1 text-sm">
              <Row label="Subtotal (ex GST)" value={formatAUDexact(subtotal)} testid="sign-row-subtotal"/>
              <Row label="GST (10%)" value={formatAUDexact(gst)} testid="sign-row-gst"/>
              <div className="flex justify-between items-baseline pt-2">
                <div className="font-display font-bold">Total inc GST</div>
                <div className="font-display font-black text-2xl price-tick" data-testid="sign-total-incgst">{formatAUDexact(total)}</div>
              </div>
            </div>
            <button onClick={addToCart} className="btn-primary w-full mt-5" data-testid="sign-add-to-cart">Add to Cart</button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function CheckList({ title, items, grid }) {
  return (
    <div className="sf-card p-6">
      <div className="eyebrow mb-3">{title}</div>
      <ul className={grid ? "grid grid-cols-2 gap-2 text-sm text-slate-700" : "space-y-2 text-sm text-slate-700"}>
        {items.map((u) => (
          <li key={u} className="flex items-center gap-2"><Check size={14} className="text-[#10B981]"/> {u}</li>
        ))}
      </ul>
    </div>
  );
}

export function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between border-b border-dashed border-[#E2DDD5] py-2" data-testid={testid}>
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-right">{value}</span>
    </div>
  );
}
